import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { GasPumpIcon, CaretRightIcon, ClockIcon, CheckCircleIcon, XCircleIcon, UserIcon } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FuelReceiptViewer from "@/components/FuelReceiptViewer";
import { detectRegion, formatVolume, formatCurrency, formatPricePerUnit, formatDistance, unitSystemFromTruck } from "@/lib/units";
import { sortByUnit } from "@/lib/unitSort";

/**
 * Live-Map companion panel: every detected fuel-up (Motive + driver receipts).
 * Filters: All / Pending / Uploaded / Dismissed, plus Truck + Driver dropdowns.
 * Click an "Uploaded" card → opens the receipt viewer modal.
 */
export default function FuelUpsPanel({ onViewerChange } = {}) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [truckId, setTruckId] = useState("all");
  const [driverId, setDriverId] = useState("all");
  const [days, setDays] = useState(30);
  const [sort, setSort] = useState("recent");
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [loading, setLoading] = useState(false);

  // Notify parent (FleetMap) so it can hide the Leaflet map while the modal
  // is open — Leaflet's canvas has its own z-index and would otherwise show
  // through the overlay.
  useEffect(() => { onViewerChange && onViewerChange(!!viewer); }, [viewer, onViewerChange]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), limit: "200", sort });
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (truckId !== "all") params.set("truck_id", truckId);
      if (driverId !== "all") params.set("driver_id", driverId);
      const { data } = await api.get(`/reports/fuel-purchases?${params}`);
      setRows(data.rows || []);
      setTotals(data.totals || {});
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [statusFilter, truckId, driverId, days, sort]);

  useEffect(() => {
    (async () => {
      const [t, d] = await Promise.all([
        api.get("/trucks"),
        api.get("/drivers"),
      ]);
      setTrucks(t.data || []);
      setDrivers(d.data || []);
    })();
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-refresh when a fuel_event changes anywhere (driver uploads a receipt,
  // dismisses, or Motive detects a new fill-up). Falls back to a 60 s poll if
  // the SSE stream is missed.
  useEffect(() => {
    const onChange = (ev) => {
      if (ev?.detail?.entity === "fuel_events") load();
    };
    window.addEventListener("rf-data-change", onChange);
    const poll = setInterval(() => load(), 60000);
    return () => {
      window.removeEventListener("rf-data-change", onChange);
      clearInterval(poll);
    };
  }, [load]);

  const tabs = useMemo(() => [
    { k: "all", label: "All", count: (totals.count ?? 0) },
    { k: "pending", label: "Pending", count: (totals.pending ?? 0), tone: "amber" },
    { k: "uploaded", label: "Uploaded", count: (totals.with_receipt ?? 0), tone: "emerald" },
    { k: "dismissed", label: "Dismissed", count: (totals.dismissed ?? 0), tone: "slate" },
  ], [totals]);

  return (
    <div className="bg-white border border-slate-300 rounded-sm" data-testid="fuel-ups-panel">
      {/* Header row */}
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-sm bg-blue-600 text-white flex items-center justify-center">
          <GasPumpIcon size={20} weight="bold" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="section-eyebrow mb-0.5">/// Fuel</div>
          <h3 className="font-display text-lg font-black tracking-tight">Fuel-ups</h3>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="h-9 rounded-sm text-xs w-32" data-testid="fuelups-days">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-9 rounded-sm text-xs w-40" data-testid="fuelups-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Newest first</SelectItem>
            <SelectItem value="rate">Rate: cheapest</SelectItem>
            <SelectItem value="rate_desc">Rate: most expensive</SelectItem>
            <SelectItem value="pump_rate">Pump rate: cheapest</SelectItem>
            <SelectItem value="pump_rate_desc">Pump rate: priciest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 border-b border-slate-100 bg-slate-50">
        <Tile label="Pending" value={totals.pending ?? 0} tone="amber" />
        <Tile label="Uploaded" value={totals.with_receipt ?? 0} tone="emerald" />
        <Tile label="Total gal" value={Number(totals.actual_gallons || 0).toLocaleString()} />
        <Tile label="Total $" value={`$${Number(totals.cost || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
      </div>

      {/* Filter chips + dropdowns */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setStatusFilter(t.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
              statusFilter === t.k
                ? "bg-blue-600 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
            data-testid={`fuelups-tab-${t.k}`}
          >
            {t.label}
            {t.count > 0 && (
              <span className={`ml-1.5 ${statusFilter === t.k ? "text-blue-100" : "text-slate-500"}`}>({t.count})</span>
            )}
          </button>
        ))}
        <div className="flex-1" />
        <Select value={truckId} onValueChange={setTruckId}>
          <SelectTrigger className="h-8 rounded-full text-xs w-32" data-testid="fuelups-truck-filter">
            <SelectValue placeholder="Truck" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All trucks</SelectItem>
            {sortByUnit(trucks).map((t) => (
              <SelectItem key={t.id} value={t.id}>#{t.unit_number}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={driverId} onValueChange={setDriverId}>
          <SelectTrigger className="h-8 rounded-full text-xs w-36" data-testid="fuelups-driver-filter">
            <SelectValue placeholder="Driver" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All drivers</SelectItem>
            {drivers.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Cards */}
      <div className="p-4 space-y-3 max-h-[720px] overflow-y-auto" data-testid="fuelups-list">
        {loading && rows.length === 0 && (
          <div className="text-center py-8 text-sm text-slate-500 font-mono">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="text-center py-8 text-sm text-slate-500">
            No fuel-ups in this window.
          </div>
        )}
        {rows.map((r) => (
          <FuelUpCard
            key={r.id}
            row={r}
            trucks={trucks}
            onView={() => setViewer(r)}
            onMarkUploaded={async (row) => {
              try {
                await api.post(`/fuel-events/${row.id}/mark-uploaded`);
                toast.success("Marked as uploaded");
                load();
              } catch (e) {
                toast.error("Could not mark as uploaded");
              }
            }}
            onDelete={async (row) => {
              if (!window.confirm(`Delete this fuel-up (Truck ${row.unit_number || "?"}, ${row.before_pct?.toFixed(0)}% → ${row.after_pct?.toFixed(0)}%)?`)) return;
              try {
                await api.delete(`/fuel-events/${row.id}`);
                toast.success("Fuel event deleted");
                load();
              } catch (e) {
                toast.error("Could not delete");
              }
            }}
          />
        ))}
      </div>

      <FuelReceiptViewer open={!!viewer} event={viewer} onClose={() => setViewer(null)} />
    </div>
  );
}

function Tile({ label, value, tone = "slate" }) {
  const map = { amber: "text-amber-700", emerald: "text-emerald-700", slate: "text-slate-900" };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3">
      <div className={`font-display font-black text-2xl tracking-tighter tabular-nums ${map[tone]}`}>{value}</div>
      <div className="font-mono text-[10px] uppercase text-slate-500 tracking-wider font-bold mt-0.5">{label}</div>
    </div>
  );
}

function FuelUpCard({ row, trucks, onView, onMarkUploaded, onDelete }) {
  const truck = trucks.find((t) => t.id === row.truck_id);
  // Unit (volume + distance) follows the TRUCK; currency follows the fill-up
  // location so a Canadian truck fueling in the US shows "US$ ... / L".
  const unitRegion = unitSystemFromTruck(truck) === "CA" || row.truck_unit_system === "metric"
    ? "CA"
    : "US";
  const currencyRegion = detectRegion({ location_address: row.location_address });
  const status = row.receipt_status || "no_driver";
  const isUploaded = status === "uploaded";
  const isPending = status === "pending";
  const isDismissed = status === "dismissed";

  const badge = isUploaded
    ? { bg: "bg-emerald-100", text: "text-emerald-700", label: "UPLOADED", icon: CheckCircleIcon }
    : isPending
    ? { bg: "bg-amber-100", text: "text-amber-700", label: "PENDING", icon: ClockIcon }
    : isDismissed
    ? { bg: "bg-slate-200", text: "text-slate-600", label: "DISMISSED", icon: XCircleIcon }
    : { bg: "bg-slate-200", text: "text-slate-600", label: "NO DRIVER", icon: UserIcon };

  const ringTone = isPending ? "border-amber-400" : "border-slate-200";
  const Icon = badge.icon;

  const when = row.recorded_at ? new Date(row.recorded_at) : null;
  const timeLabel = when ? `${when.toLocaleDateString(undefined, { month: "short", day: "numeric" })} at ${when.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "—";

  const gallons = row.actual_gallons ?? row.estimated_gallons;
  const usedEstimate = row.actual_gallons == null && row.estimated_gallons != null;

  return (
    <div
      onClick={isUploaded ? onView : undefined}
      className={`w-full text-left bg-white rounded-sm border-2 ${ringTone} p-4 transition-colors ${
        isUploaded ? "hover:border-blue-500 cursor-pointer" : "cursor-default"
      }`}
      data-testid={`fuelup-card-${row.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-sm bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
            <GasPumpIcon size={18} weight="bold" className="text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-slate-900 truncate">
              Truck #{truck?.unit_number || row.unit_number || "?"}
              {row.mileage && <span className="text-slate-500 font-mono font-normal ml-1"> · {formatDistance(row.mileage, unitRegion, 0)}</span>}
            </div>
            <div className="text-xs text-slate-600 font-mono uppercase tracking-wide truncate">
              {row.driver_name && row.driver_name !== "—" ? `${row.driver_name} · ` : ""}{timeLabel}
            </div>
          </div>
        </div>
        <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${badge.bg} ${badge.text} flex-shrink-0`}>
          <Icon size={12} weight="fill" />
          {badge.label}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <MiniStat label="Before" value={row.before_pct != null ? `${Math.round(row.before_pct)}%` : "—"} />
        <MiniStat label="After" value={row.after_pct != null ? `${Math.round(row.after_pct)}%` : "—"} />
        <MiniStat
          label={usedEstimate ? (unitRegion === "CA" ? "Est. L" : "Est. gal") : (unitRegion === "CA" ? "Actual L" : "Actual gal")}
          value={gallons != null ? formatVolume(gallons, unitRegion, 1).replace(/ (gal|L)$/, "") : "—"}
          highlight={!usedEstimate}
        />
        <MiniStat
          label="Cost"
          value={row.cost != null ? formatCurrency(row.cost, currencyRegion) : "—"}
          highlight
        />
      </div>

      {((gallons && row.cost) || row.sheet_rate != null) && (
        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] font-mono" data-testid={`fuelup-rate-${row.id}`}>
          <span className="text-slate-500">
            Your rate{" "}
            {gallons && row.cost
              ? <span className="font-bold text-blue-700">{formatPricePerUnit(Number(row.cost) / Number(gallons), unitRegion, 3, currencyRegion)}</span>
              : <span className="text-slate-400">—</span>}
          </span>
          {row.sheet_rate != null && (
            <span className="text-slate-500" title={`${row.sheet_rate_vendor || ""}${row.sheet_rate_site ? ` #${row.sheet_rate_site}` : ""} · ${row.sheet_rate_city || ""}`}>
              Pump today{" "}
              <span className="font-bold text-emerald-700">{formatPricePerUnit(row.sheet_rate, unitRegion, 3, currencyRegion)}</span>
            </span>
          )}
        </div>
      )}

      {isUploaded && (
        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
          <div className="text-[11px] font-mono text-slate-500 truncate">
            {row.receipt_station || ""}
          </div>
          <div className="text-xs font-bold text-blue-600 inline-flex items-center gap-1">
            View receipt <CaretRightIcon size={12} weight="bold" />
          </div>
        </div>
      )}
      {(isPending || status === "no_driver") && (onMarkUploaded || onDelete) && (
        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-end gap-2">
          {onMarkUploaded && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onMarkUploaded(row); }}
              className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 border border-emerald-200 hover:border-emerald-400 rounded-sm px-2 py-1 transition-colors"
              data-testid={`mark-uploaded-${row.id}`}
              title="Mark as uploaded (paper receipt on file or manual confirmation)"
            >
              Mark uploaded
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(row); }}
              className="text-[11px] font-semibold text-red-700 hover:text-red-900 border border-red-200 hover:border-red-400 rounded-sm px-2 py-1 transition-colors"
              data-testid={`delete-event-${row.id}`}
              title="Delete this fuel event (false positive)"
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, highlight = false }) {
  return (
    <div className="bg-slate-50 rounded-sm p-2 text-center">
      <div className={`font-bold text-sm tabular-nums ${highlight ? "text-blue-600" : "text-slate-900"}`}>{value}</div>
      <div className="font-mono text-[9px] uppercase text-slate-500 tracking-wider font-bold mt-0.5">{label}</div>
    </div>
  );
}
